import {
  PeriodType,
  randomInt,
  randomFloat,
  shuffle,
  pointCountForPeriod,
  buildWaveSeries,
  distributeTotal,
  buildWeightedRecord,
  pointsFromSeries,
  scaleForPeriod,
  formatCurrencyBRL,
  objectFromEntries,
  PRODUCT_NAMES,
  BR_CITIES,
  CITY_NAMES,
  COUNTRY_NAMES,
  TRAFFIC_SOURCES,
  CAMPAIGN_NAMES,
} from "./mockShared";

// Dados fictícios para as páginas de Relatórios na conta Preview.
// Os formatos abaixo espelham o que os adapters em src/service/adapters
// esperam receber de cada endpoint real. Os campos calculados (ROAS, CPA,
// CTR, detalhamentos por fonte/página/item) derivam dos mesmos números
// base em vez de serem sorteados de forma independente, para os valores
// fazerem sentido entre si (ex.: soma por fonte ≈ total, cliques ≤
// impressões, ticket médio do gráfico ≈ o do card).
const buildCards = (period: PeriodType) => {
  const scale = scaleForPeriod(period);
  const totalVisitors = Math.max(20, Math.round(randomInt(800, 1400) * scale));
  const submits = Math.round(totalVisitors * randomFloat(0.08, 0.14));
  const leads = Math.round(submits * randomFloat(0.5, 0.75));
  const placedOrders = Math.round(leads * randomFloat(0.25, 0.45));
  const avgTicket = randomFloat(120, 260);
  const revenue = placedOrders * avgTicket;
  const googleInvestment = randomFloat(400, 900) * scale;
  const metaInvestment = randomFloat(300, 700) * scale;
  const totalInvestment = googleInvestment + metaInvestment;
  const roas = totalInvestment > 0 ? (revenue / totalInvestment) * 100 : null;
  const cpa = placedOrders > 0 ? totalInvestment / placedOrders : null;
  const realUsersRate = randomInt(85, 100);

  return {
    GoogleAdsInvestment: formatCurrencyBRL(googleInvestment),
    MetaAdsInvestment: formatCurrencyBRL(metaInvestment),
    TotalInvestment: formatCurrencyBRL(totalInvestment),
    PlacedOrders: placedOrders,
    Revenue: formatCurrencyBRL(revenue),
    AverageTicket: formatCurrencyBRL(avgTicket),
    Leads: leads,
    Submits: submits,
    TotalVisitors: totalVisitors,
    RealUsersRate: realUsersRate,
    ROAS: roas != null ? Number(roas.toFixed(2)) : null,
    CPA: cpa != null ? Number(cpa.toFixed(2)) : null,
    // Valores numéricos brutos (não fazem parte do contrato dos adapters,
    // usados só internamente para gerar gráficos/detalhamentos coerentes).
    revenueValue: revenue,
    avgTicketValue: avgTicket,
    totalInvestmentValue: totalInvestment,
    googleInvestmentValue: googleInvestment,
    metaInvestmentValue: metaInvestment,
  };
};

// Gera uma série com base numa média por ponto (total / nº de pontos do
// período), sempre com piso mínimo, para o gráfico ficar visualmente cheio
// do início ao fim em vez de só aparecer no fim.
const buildMetricChart = (
  period: PeriodType,
  total: number,
  options?: { minAbsolute?: number; floorRatio?: number },
) => {
  const count = pointCountForPeriod(period);
  const series = buildWaveSeries(period, total / count, {
    minAbsolute: options?.minAbsolute ?? 2,
    floorRatio: options?.floorRatio ?? 0.55,
  });
  return pointsFromSeries(period, series);
};

// Gera byImpressions/byClick(/byCost) para uma lista de categorias a partir
// de um total de impressões real (o mesmo usado no gráfico de impressões),
// com cliques como uma fração plausível de CTR de cada categoria — em vez de
// sorteados de forma independente (o que podia dar clique > impressão).
const buildClickImpressionBreakdown = (
  categories: string[],
  totalImpressions: number,
  withCost: boolean,
) => {
  const impressionsByCategory = distributeTotal(totalImpressions, categories.length, {
    minEach: 5,
  });
  const byImpressions: Record<string, number> = {};
  const byClick: Record<string, number> = {};
  const byCost: Record<string, number> = {};

  categories.forEach((key, index) => {
    const impressions = impressionsByCategory[index];
    const clicks = Math.max(1, Math.round(impressions * randomFloat(0.03, 0.09)));
    byImpressions[key] = impressions;
    byClick[key] = clicks;
    if (withCost) {
      byCost[key] = Number((clicks * randomFloat(0.6, 1.8)).toFixed(2));
    }
  });

  return withCost ? { byClick, byImpressions, byCost } : { byClick, byImpressions };
};

// Chaves em maiúsculas: é o formato usado pela página do Google Ads
// (ver src/app/Analitycs/GoogleAds/page.tsx).
const buildGoogleDeviceMetrics = (totalImpressions: number) =>
  buildClickImpressionBreakdown(
    ["MOBILE", "DESKTOP", "TABLET", "CONNECTED_TV", "OTHER"],
    totalImpressions,
    true,
  );

// Chaves em snake_case minúsculo: formato usado pela página do Meta Ads
// (ver src/app/Analitycs/MetaAds/page.tsx).
const buildMetaDeviceMetrics = (totalImpressions: number) =>
  buildClickImpressionBreakdown(["mobile_app", "mobile_web", "desktop"], totalImpressions, true);

const buildGenderMetrics = (totalImpressions: number) =>
  buildClickImpressionBreakdown(
    ["Feminino", "Masculino", "Não informado"],
    totalImpressions,
    false,
  );

const buildAgeRangeMetrics = (totalImpressions: number) =>
  buildClickImpressionBreakdown(
    ["18-24", "25-34", "35-44", "45-54", "55+"],
    totalImpressions,
    false,
  );

export const mockOverviewMetrics = (period: PeriodType) => {
  const cards = buildCards(period);
  const count = pointCountForPeriod(period);

  const accessChart = buildMetricChart(period, cards.TotalVisitors, { minAbsolute: 3 });
  const ordersChart = buildMetricChart(period, cards.PlacedOrders, { minAbsolute: 1, floorRatio: 0.45 });
  // Médias por ponto derivadas dos mesmos valores do card, para o gráfico
  // combinar com o faturamento/ticket médio/ROAS/CPA mostrados nos números.
  const revenueSeries = buildWaveSeries(period, cards.revenueValue / count, { minAbsolute: 40 });
  const avgTicketSeries = buildWaveSeries(period, cards.avgTicketValue, { minAbsolute: 60, floorRatio: 0.75 });
  const roasSeries = buildWaveSeries(period, cards.ROAS ?? 150, { minAbsolute: 20, floorRatio: 0.6 });
  const cpaSeries = buildWaveSeries(period, cards.CPA ?? 30, { minAbsolute: 5, floorRatio: 0.6 });

  return {
    data: {
      cards,
      tagsMetrics: {
        // Cada visita vem de uma única fonte, então a soma bate com o total
        // de visitantes; pedidos idem, com o total de pedidos do card.
        visitors: buildWeightedRecord(TRAFFIC_SOURCES, cards.TotalVisitors),
        orders: buildWeightedRecord(TRAFFIC_SOURCES, cards.PlacedOrders),
      },
      adsMetrics: {
        google: CAMPAIGN_NAMES.map((campaign) => ({
          ad: `${campaign} - Anúncio`,
          campaign,
          customerId: String(randomInt(1000000000, 9999999999)),
          impression: randomInt(1000, 8000),
        })),
        meta: CAMPAIGN_NAMES.map((campaign) => ({
          ad: `${campaign} - Criativo`,
          campaign,
          adAccountId: String(randomInt(100000000000000, 999999999999999)),
          reach: randomInt(1000, 8000),
        })),
      },
      accessEvTimeChart: accessChart,
      revenueEvTimeChart: {
        period,
        points: pointsFromSeries(period, revenueSeries).points.map((p) => ({
          x: p.x,
          y: formatCurrencyBRL(p.y),
        })),
      },
      ordersEvTimeChart: ordersChart,
      averageTicketEvTimeChart: {
        period,
        points: pointsFromSeries(period, avgTicketSeries).points.map((p) => ({
          x: p.x,
          y: formatCurrencyBRL(p.y),
        })),
      },
      roasEvTimeChart: pointsFromSeries(period, roasSeries),
      cpaEvTimeChart: pointsFromSeries(period, cpaSeries),
    },
  };
};

export const mockSiteMetrics = (period: PeriodType) => {
  const cards = buildCards(period);
  const totalVisitors = cards.TotalVisitors;

  // Espalha as visitas por várias capitais de diferentes regiões do Brasil
  // (em vez de cidades próximas umas das outras) para o mapa ficar rico, com
  // a soma batendo com o total de visitantes do card (mesma base de dados).
  const selectedCities = shuffle(BR_CITIES).slice(0, randomInt(7, BR_CITIES.length));
  const geolocationValues = distributeTotal(totalVisitors, selectedCities.length, { minEach: 1 });
  const geolocationEntries = selectedCities.map(
    (city, index) =>
      [`${city.name},${city.state},Brasil/${city.lat.toFixed(4)},${city.lon.toFixed(4)}`, geolocationValues[index]] as [
        string,
        number,
      ],
  );

  return {
    data: {
      cards,
      accessEvTimeChart: buildMetricChart(period, totalVisitors, { minAbsolute: 3 }),
      visitsPerWeekDayChart: {
        period,
        points: pointsFromSeries("last7", buildWaveSeries("last7", totalVisitors / 7, { minAbsolute: 20 })).points,
      },
      visitsPerHourChart: {
        period,
        points: pointsFromSeries("today", buildWaveSeries("today", totalVisitors / 24, { minAbsolute: 3 })).points,
      },
      // Uma visita pode passar por várias páginas — soma um pouco acima do
      // total de visitantes, mas ainda numa proporção plausível (~1.3-2.2x).
      visitedPagesMetrics: buildWeightedRecord(
        ["/", "/produtos", "/produto/camiseta-oversized", "/carrinho", "/checkout"],
        Math.round(totalVisitors * randomFloat(1.3, 2.2)),
      ),
      // Nem toda visita chega por um link com referrer.
      referrerPagesMetrics: buildWeightedRecord(
        ["instagram.com", "google.com", "whatsapp.com", "facebook.com"],
        Math.round(totalVisitors * randomFloat(0.3, 0.6)),
      ),
      // Uma fonte, um dispositivo, um navegador, um SO, um país por visita —
      // a soma bate com o total de visitantes.
      trafficSourceMetrics: buildWeightedRecord(TRAFFIC_SOURCES, totalVisitors),
      deviceMetrics: buildWeightedRecord(["Mobile", "Desktop", "Tablet"], totalVisitors),
      browserMetrics: buildWeightedRecord(["Chrome", "Safari", "Firefox", "Edge"], totalVisitors),
      osMetrics: buildWeightedRecord(["Android", "iOS", "Windows", "macOS"], totalVisitors),
      countryMetrics: buildWeightedRecord(COUNTRY_NAMES, totalVisitors),
      cityMetrics: buildWeightedRecord(CITY_NAMES, totalVisitors),
      exitPagesMetrics: buildWeightedRecord(
        ["/checkout", "/carrinho", "/produtos"],
        Math.round(totalVisitors * randomFloat(0.5, 0.9)),
      ),
      geolocationMetrics: objectFromEntries(geolocationEntries),
    },
  };
};

export const mockTagsMetrics = (period: PeriodType) => {
  const cards = buildCards(period);

  // Visitantes com tag por fonte somam ~60% do total; a quantidade de tags
  // nunca passa do número de visitantes daquela fonte (nem toda visita tem
  // mais de uma tag).
  const visitorsWithTagsTotal = Math.round(cards.TotalVisitors * 0.6);
  const visitorsBySource = distributeTotal(visitorsWithTagsTotal, TRAFFIC_SOURCES.length);

  return {
    data: {
      cards,
      visitorsWithTagsChart: buildMetricChart(period, visitorsWithTagsTotal, { minAbsolute: 2 }),
      tagsPerTrafficSourceMetrics: TRAFFIC_SOURCES.reduce<
        Record<string, { tagsQuantity: number; visitors: number }>
      >((acc, source, index) => {
        const visitors = visitorsBySource[index];
        acc[source] = {
          visitors,
          tagsQuantity: Math.round(visitors * randomFloat(0.4, 0.9)),
        };
        return acc;
      }, {}),
    },
  };
};

const buildFunnelStep = (
  name: string,
  count: number,
  trafficSource?: string,
) => ({
  name,
  ...(trafficSource ? { trafficSource } : {}),
  count,
  previous: {},
});

// Tags genéricas (à semelhança de UTMs de campanha) usadas para popular a
// árvore de "Análise detalhada de caminho" com dados plausíveis para
// qualquer fonte de tráfego selecionada.
const FUNNEL_TAG_NAMES = [
  "promo-verao",
  "black-friday",
  "influencer-parceiro",
  "email-mkt",
  "cupom-primeira-compra",
];

// Mesmas tags em todas as fontes de tráfego fixas — só o "Geral" (que usa
// buildGeneralFunnelTree, sem tags) é diferente nos mockups. Os números de
// cada tag são uma fração dos números da própria fonte (nem toda visita tem
// tag), então nunca ultrapassam o total daquela fonte.
const buildByTag = (
  sourceTotals: Record<string, { submits: number; leads: number; orders: number }>,
): Record<string, ReturnType<typeof buildFunnelStep>[]> => {
  const byTag: Record<string, ReturnType<typeof buildFunnelStep>[]> = {};

  TRAFFIC_SOURCES.forEach((source) => {
    const sourceTotal = sourceTotals[source];
    const taggedShare = randomFloat(0.55, 0.85);
    const submitsByTag = distributeTotal(
      Math.round(sourceTotal.submits * taggedShare),
      FUNNEL_TAG_NAMES.length,
    );
    const leadsByTag = distributeTotal(
      Math.round(sourceTotal.leads * taggedShare),
      FUNNEL_TAG_NAMES.length,
    );
    const ordersByTag = distributeTotal(
      Math.round(sourceTotal.orders * taggedShare),
      FUNNEL_TAG_NAMES.length,
    );

    FUNNEL_TAG_NAMES.forEach((tag, index) => {
      const entries = [
        buildFunnelStep("submit", submitsByTag[index], source),
        buildFunnelStep("lead", leadsByTag[index], source),
        buildFunnelStep("orderplaced", ordersByTag[index], source),
      ];

      byTag[tag] = [...(byTag[tag] || []), ...entries];
    });
  });

  return byTag;
};

export const mockSalesFunnelMetrics = (period: PeriodType) => {
  const cards = buildCards(period);

  const total = [
    buildFunnelStep("submit", cards.Submits),
    buildFunnelStep("lead", cards.Leads),
    buildFunnelStep("orderplaced", cards.PlacedOrders),
  ];

  // A soma dos envios de formulário por fonte bate com o total do card;
  // leads/pedidos de cada fonte são frações do próprio submit da fonte.
  const byTrafficSourceSteps: Record<string, ReturnType<typeof buildFunnelStep>[]> = {};
  const sourceTotals: Record<string, { submits: number; leads: number; orders: number }> = {};
  const submitsBySource = distributeTotal(cards.Submits, TRAFFIC_SOURCES.length);

  TRAFFIC_SOURCES.forEach((source, index) => {
    const sourceSubmits = submitsBySource[index];
    const sourceLeads = Math.round(sourceSubmits * randomFloat(0.5, 0.8));
    const sourceOrders = Math.round(sourceLeads * randomFloat(0.2, 0.4));
    sourceTotals[source] = { submits: sourceSubmits, leads: sourceLeads, orders: sourceOrders };
    byTrafficSourceSteps[source] = [
      buildFunnelStep("submit", sourceSubmits),
      buildFunnelStep("lead", sourceLeads),
      buildFunnelStep("orderplaced", sourceOrders),
    ];
  });

  const CAPTURE_PAGES = [
    { url: "/", forms: [{ id: "1", label: "Assinar newsletter" }] },
    {
      url: "/checkout",
      forms: [
        { id: "2", label: "Botão finalizar compra" },
        { id: "3", label: "Formulário de contato" },
      ],
    },
    {
      url: "/contato",
      forms: [
        { id: "4", label: "Falar com um vendedor" },
        { id: "5", label: "Solicitar orçamento" },
      ],
    },
    {
      url: "/produto/camiseta-oversized",
      forms: [{ id: "6", label: "Avise-me quando chegar" }],
    },
    {
      url: "/carrinho",
      forms: [{ id: "7", label: "Cupom de desconto" }],
    },
  ];

  // Distribui o total de leads do card entre todos os formulários de
  // captura, em vez de sortear cada contagem sem relação com o card.
  const totalFormEntries = CAPTURE_PAGES.reduce((sum, page) => sum + page.forms.length, 0);
  const formCounts = distributeTotal(
    Math.round(cards.Leads * randomFloat(0.8, 1.4)),
    totalFormEntries,
    { minEach: 1 },
  );
  let formCountCursor = 0;
  const leadMetricsPerPage = CAPTURE_PAGES.reduce<
    Record<string, Array<{ id: string; label: string; count: number }>>
  >((acc, page) => {
    acc[page.url] = page.forms.map((form) => ({
      ...form,
      count: formCounts[formCountCursor++],
    }));
    return acc;
  }, {});

  return {
    data: {
      cards,
      salesFunnel: {
        total,
        byTrafficSource: byTrafficSourceSteps,
        byTag: buildByTag(sourceTotals),
      },
      leadMetricsPerPage,
    },
  };
};

export const mockGoogleAdsMetrics = (period: PeriodType) => {
  const cards = buildCards(period);
  const totalImpressions = Math.round(randomInt(3000, 9000) * scaleForPeriod(period));
  const accessedTagsTotal = Math.round(cards.TotalVisitors * randomFloat(0.5, 0.9));
  const convertingTagsTotal = Math.round(accessedTagsTotal * randomFloat(0.1, 0.3));
  // Mesmas impressões usadas no gráfico, distribuídas entre as campanhas —
  // cliques como fração de CTR realista (nunca ultrapassam as impressões).
  const campaignImpressions = distributeTotal(totalImpressions, CAMPAIGN_NAMES.length, {
    minEach: 10,
  });
  const withRealisticClicks = (impressions: number) =>
    Math.max(1, Math.round(impressions * randomFloat(0.02, 0.08)));

  return {
    data: {
      cards,
      impressionsChart: buildMetricChart(period, totalImpressions, { minAbsolute: 30 }),
      topAccessedTags: buildWeightedRecord(TRAFFIC_SOURCES, accessedTagsTotal),
      topConvertingTags: buildWeightedRecord(TRAFFIC_SOURCES, convertingTagsTotal),
      topCampaigns: CAMPAIGN_NAMES.map((name, index) => ({
        name,
        customerId: String(randomInt(1000000000, 9999999999)),
        impressions: campaignImpressions[index],
        clicks: withRealisticClicks(campaignImpressions[index]),
      })),
      topAds: CAMPAIGN_NAMES.map((campaign, index) => ({
        name: `${campaign} - Anúncio`,
        campaign,
        customerId: String(randomInt(1000000000, 9999999999)),
        impressions: campaignImpressions[index],
        clicks: withRealisticClicks(campaignImpressions[index]),
      })),
      deviceMetrics: buildGoogleDeviceMetrics(totalImpressions),
      genderMetrics: buildGenderMetrics(totalImpressions),
      ageRangeMetrics: buildAgeRangeMetrics(totalImpressions),
    },
  };
};

export const mockMetaAdsMetrics = (period: PeriodType) => {
  const cards = buildCards(period);
  const totalImpressions = Math.round(randomInt(3000, 9000) * scaleForPeriod(period));
  const accessedTagsTotal = Math.round(cards.TotalVisitors * randomFloat(0.5, 0.9));
  const convertingTagsTotal = Math.round(accessedTagsTotal * randomFloat(0.1, 0.3));
  const campaignImpressions = distributeTotal(totalImpressions, CAMPAIGN_NAMES.length, {
    minEach: 10,
  });
  const adsReach = distributeTotal(
    Math.round(totalImpressions * randomFloat(0.5, 0.8)),
    CAMPAIGN_NAMES.length,
    { minEach: 5 },
  );
  const withRealisticClicks = (impressions: number) =>
    Math.max(1, Math.round(impressions * randomFloat(0.02, 0.08)));

  return {
    data: {
      cards,
      impressionsChart: buildMetricChart(period, totalImpressions, { minAbsolute: 30 }),
      adsMetrics: CAMPAIGN_NAMES.map((campaign, index) => ({
        adName: `${campaign} - Criativo`,
        adAccountId: String(randomInt(100000000000000, 999999999999999)),
        frequency: randomFloat(1, 4),
        ctr: randomFloat(0.5, 4),
        cpc: formatCurrencyBRL(randomFloat(0.5, 3)),
        reach: adsReach[index],
      })),
      topAccessedTags: buildWeightedRecord(TRAFFIC_SOURCES, accessedTagsTotal),
      topConvertingTags: buildWeightedRecord(TRAFFIC_SOURCES, convertingTagsTotal),
      topCampaigns: CAMPAIGN_NAMES.map((name, index) => ({
        name,
        adAccountId: String(randomInt(100000000000000, 999999999999999)),
        clicks: withRealisticClicks(campaignImpressions[index]),
        impressions: campaignImpressions[index],
      })),
      topAds: CAMPAIGN_NAMES.map((campaign, index) => ({
        name: `${campaign} - Criativo`,
        campaign,
        adAccountId: String(randomInt(100000000000000, 999999999999999)),
        clicks: withRealisticClicks(campaignImpressions[index]),
        impressions: campaignImpressions[index],
      })),
      deviceMetrics: buildMetaDeviceMetrics(totalImpressions),
      genderMetrics: buildGenderMetrics(totalImpressions),
      ageRangeMetrics: buildAgeRangeMetrics(totalImpressions),
    },
  };
};

export const mockShowcaseMetrics = (period: PeriodType) => {
  const totalAccess = randomInt(300, 1200);
  const totalClicks = Math.round(totalAccess * randomFloat(0.15, 0.35));
  // Média do gráfico de CTR combina com clicks/access reais, em vez de um
  // valor fixo desconectado dos outros números.
  const avgCtr = totalAccess > 0 ? (totalClicks / totalAccess) * 100 : 20;
  // Links/produtos mais clicados são uma fração dos cliques totais; cidades
  // e países são uma fração dos acessos totais.
  const topLinksTotal = Math.round(totalClicks * randomFloat(0.5, 0.85));
  const topProductsTotal = Math.round(totalClicks * randomFloat(0.3, 0.6));
  const cityTotal = Math.round(totalAccess * randomFloat(0.6, 0.95));

  return {
    data: {
      accessEvTimeChart: buildMetricChart(period, totalAccess, { minAbsolute: 3 }),
      clickEvTimeChart: buildMetricChart(period, totalClicks, { minAbsolute: 1, floorRatio: 0.45 }),
      ctrEvTimeChart: pointsFromSeries(
        period,
        buildWaveSeries(period, avgCtr, {
          minAbsolute: Math.max(2, avgCtr * 0.3),
          floorRatio: 0.6,
          integer: false,
        }),
      ),
      dwellTimeEvTimeChart: pointsFromSeries(period, buildWaveSeries(period, 90, { minAbsolute: 15, floorRatio: 0.5 })),
      topLinks: buildWeightedRecord(
        ["https://instagram.com/perfil", "https://loja.com.br/promo", "https://wa.me/5511999999999"],
        topLinksTotal,
      ),
      topProducts: buildWeightedRecord(
        PRODUCT_NAMES.map((name) => `https://loja.com.br/produto/${name.toLowerCase().replace(/\s+/g, "-")}`),
        topProductsTotal,
      ),
      cityMetrics: buildWeightedRecord(CITY_NAMES, cityTotal),
      countryMetrics: buildWeightedRecord(COUNTRY_NAMES, totalAccess),
      trafficChannelMetrics: buildWeightedRecord(["instagram", "google", "facebook", "whatsapp", "site-blog"], totalAccess),
      clickedLinks: [
        "https://loja.com.br/promo",
        "https://instagram.com/perfil",
      ],
      averageTimeToClick: randomInt(5, 40),
      nonInteractiveVisitRate: randomFloat(10, 40),
      deadClickRate: randomFloat(1, 12),
      realUsersRate: randomInt(85, 100),
    },
  };
};

export const mockAllTagTrafficMetrics = (
  period: PeriodType,
  trafficSource: string,
) => {
  const tags = ["promo-verao", "black-friday", "influencer-x", "<UNKNOWN>"];

  const data = tags.map((tag) => {
    const visitCount = randomInt(50, 500);
    const leadCount = Math.round(visitCount * randomFloat(0.05, 0.15));
    const orderCount = Math.round(leadCount * randomFloat(0.2, 0.4));
    const orderRevenueMicros = orderCount * randomInt(80, 250) * 1_000_000;

    return {
      tag,
      visitCount,
      leadCount,
      orderCount,
      orderRevenueMicros,
    };
  });

  return {
    data,
    pagination: { page: 1, total: 1 },
  };
};
